import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import cx from 'classnames';
import ClampLines from '../ClampLines/ClampLines';

import TranslationProvider, {
  getTranslation,
} from '../../providers/TranslationProvider';

import Button from '../Button';
import Icon from '../Icon';
import Image from '../Image';
import Link from '../Link';
import BadgeCollection from '../BadgeCollection';
import FavoriteButton from '../FavoriteButton';
import Rating from '../Rating';
import ReservationButtonList from '../ReservationButtonList';
import { priceRangeToString, getLocationSlug, getPageContext } from '../../utils';
import openTockUrl from '../../utils/openTockUrl';
import { LIST_DETAIL } from '../../utils/getPageContext';
import slotProps from '../../props/slots';
import pinSVG from '../../svg/icons/ic_location_20x20.svg';
import GdaCircleLogo from '../../svg/icons/gda_circle_logo.svg';
import TockBadge from '../TockBadge';
import ExperienceChip from '../ExperienceChip';
import { openTockExperienceOffering } from '../../reducers/tockExperienceSlice';

import './SearchResult.scss';
import Eyebrow from '../Eyebrow';
import { setReservation } from '../../reducers';
import { setSearchParams, getURLSearchParams } from '../../utils/searchParamUtils';
import { findSlotFromSlots } from '../../utils/bookingHelpers';

let loginThroughGatedSlotClickTimer = null;

const testId = 'search-result';
const SearchResult = ({
  $rootScope,
  activeNotifications,
  canFavorite,
  columnMetadata,
  compact,
  context,
  event,
  eyebrow,
  layout,
  onVenueClick,
  isFavorite,
  isMobile,
  isSuggestion,
  onFavoriteClick,
  onReservationButtonClick,
  onVenueCardHover,
  reservationLimit,
  selectedDate,
  selectedSeats,
  showBadges,
  showBorder,
  showNotify,
  showPhoto,
  showRating,
  showSlots,
  smallImage,
  userIsGda,
  venue,
  venueEvents,
  baseUrl,
  isAuthenticated,
  showAuthModal,
  setGatingError,
}) => {
  const dispatch = useDispatch();
  const isTockInventory = venue.isTockInventory === true;
  const hasTockExperiences = isTockInventory && (venue.experiences?.length > 0);
  const priceRange = priceRangeToString(venue.priceRange, venue.currency);
  const hasPriceRange = Boolean(priceRange);
  const searchParams = getURLSearchParams();
  const token = searchParams.get('token');
  const exclusiveId = searchParams.get('exclusiveId');
  const venueNameParam = searchParams.get('venueName');
  const scrollY = searchParams.get('scrollY');

  const getVenueHref = () => (isTockInventory
    ? venue.tockBusinessPageUrl
    : `${baseUrl || ''}cities/${getLocationSlug(venue.location)}/venues/${venue.urlSlug}?date=${moment(selectedDate).format('YYYY-MM-DD')}&seats=${selectedSeats}`);

  const onReservationSelect = (reservation, stId, notifyService, targetElement) => {
    const reservationConfig = {};
    reservationConfig.venueId = venue.id;
    reservationConfig.src = 'resy.com-venue-card';
    reservationConfig.date = moment(selectedDate).format('YYYY-MM-DD');
    reservationConfig.seats = selectedSeats;

    onReservationButtonClick?.();

    if (reservation) {
      reservationConfig.tableConfigId = reservation.token;
    }

    if (notifyService) {
      $rootScope.$broadcast('MODAL', 'notify', {
        activeNotifications,
        locationSlug: venue.location.urlSlug,
        selectedDate,
        selectedQuantity: selectedSeats,
        notifyService,
        venueId: venue.id,
        venueName: venue.name,
        venueUrl: venue.urlSlug,
      });
      return;
    }

    const reservationDetails = (reservation && typeof (reservation) === 'object') ? { ...reservation, venueName: venue.name } : reservation;

    if (reservation && !reservation.isEligible) {
      if (isAuthenticated) {
        setGatingError({ ...reservation, venueName: venue.name });
        $rootScope.$broadcast('MODAL', 'gating-error');
      } else {
        setSearchParams('token', reservation.token);
        setSearchParams('exclusiveId', reservation.exclusiveId);
        setSearchParams('venueName', venue?.name);
        setSearchParams('scrollY', window.scrollY);
        showAuthModal();
      }
    } else {
      resyWidget.openModal(reservationConfig, reservationDetails, targetElement);
    }
  };

  useEffect(() => {
    const clearTokenAndExclusiveId = () => {
      setSearchParams('token', null);
      setSearchParams('exclusiveId', null);
      setSearchParams('venueName', null);
      setSearchParams('scrollY', null);
    };

    const loginThroughGatedSlotClick = (reservations, tokenParam, exclusiveIdParam) => {
      const slotFound = findSlotFromSlots(reservations, tokenParam, Number(exclusiveIdParam));
      if (slotFound) {
        loginThroughGatedSlotClickTimer = setTimeout(() => {
          window.scrollTo(0, scrollY);
          onReservationSelect(slotFound, null, null, null);
          clearTokenAndExclusiveId();
        }, 1000);
      } else if (venueNameParam === venue?.name) {
        loginThroughGatedSlotClickTimer = setTimeout(() => {
          clearTokenAndExclusiveId();
        }, 1000);
      }
    };

    if (venue?.slots && token && venueNameParam === venue?.name && isAuthenticated) {
      loginThroughGatedSlotClick(venue.slots, token, exclusiveId);
    }
  }, [token, isAuthenticated]);

  useEffect(() => () => {
    clearTimeout(loginThroughGatedSlotClickTimer);
  }, []);

  const handleFavoriteClick = (params) => {
    onFavoriteClick({
      ...params,
      venue_id: venue.id,
      venue,
    });
  };

  const renderFavoriteButton = () => {
    if (!canFavorite || isTockInventory) return null;

    return (
      <FavoriteButton
        on={isFavorite}
        onFavoriteClick={handleFavoriteClick}
        venueName={venue.name}
      />
    );
  };

  const renderCollections = () => {
    if (!showBadges || isTockInventory) return null;

    if (venue.collections && venue.collections.length > 0) {
      return (
        <div className={cx('SearchResult__collections', {
          'SearchResult__collections--full-width': isSuggestion && (isMobile || compact),
        })}
        >
          <BadgeCollection
            badges={venue.collections}
            showLabels={false}
          />
        </div>
      );
    }
    return null;
  };

  const renderWhyWeLikeIt = (events = []) => {
    // Lists layout doesn't display slots and should display 5 lines instead of 2
    const linesToDisplay = (isMobile || showSlots) ? 2 : 4;
    if (venue.whyWeLikeIt && events.length === 0) {
      // need to leave a line for attribution if available
      const hasAttribution = !!(venue.whyWeLikeIt.attribution);
      return (
        <div
          className="SearchResult__why-we-like-it body--sm color--text-secondary"
          aria-label={venue.whyWeLikeIt.body}
          /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
          tabIndex={0}
        >
          <ClampLines
            text={venue.whyWeLikeIt.body}
            id={`venue-why-we-like-it_${venue.urlSlug}`}
            lines={(hasAttribution) ? linesToDisplay - 1 : linesToDisplay}
            buttons={false}
            ellipsis="..."
          />
          {venue.whyWeLikeIt.attribution && (
            <span
              aria-label={venue.whyWeLikeIt.attribution}
            >
              &nbsp;&mdash;&nbsp;
              {venue.whyWeLikeIt.attribution}
            </span>
          )}
        </div>
      );
    }
    return null;
  };

  const renderEvents = (events) => {
    if (!events) {
      return null;
    }

    const onlyOneEvent = events.length === 1;
    if (events.length > 3) {
      events = events.slice(0, 3);
    }

    return (
      <div className="events-container">
        {events.map((eventData) => {
          const locationSlug = getLocationSlug(venue.location);
          const venueName = venue.urlSlug;

          return (
            <div className={cx('event-container', {
              'event-container__half-size': events.length === 2,
              'event-container__third-size': events.length === 3,
            })}
            >
              {onlyOneEvent
                && <Image className="event-image" src={eventData.image_preview} />}
              <div className={cx('details', { 'details__with-image': onlyOneEvent })}>
                <div className="inner-details">
                  <Link
                    aria-label={`Link to event: ${eventData.name}`}
                    href={`/cities/${locationSlug}/venues/${venueName}/events/${eventData.url_slug}`}
                    data-test-id={`${testId}-link-event-details`}
                  >
                    <div className="event-type">
                      <TranslationProvider translationKey="event">
                        <span className="event-type">
                          Event
                        </span>
                      </TranslationProvider>
                    </div>
                  </Link>
                  <div className="event-name">{eventData.name}</div>
                </div>
                {onlyOneEvent
                  && (
                    <TranslationProvider translationKey="learn_more">
                      <div className="cta">Learn More</div>
                    </TranslationProvider>
                  )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const toggleGdaConciergeModal = () => {
    $rootScope.$broadcast('MODAL', 'concierge-booking');
  };

  const getButtonLimit = () => {
    if (isMobile) {
      return 3;
    }

    return reservationLimit;
  };

  const handleExperienceChipClick = (experience) => {
    dispatch(openTockExperienceOffering({
      selectedExperience: experience,
      venueId: venue?.id || null,
      venueName: venue?.name || null,
      selectedDate,
      selectedSeats,
      isMobile,
    }));
  };

  const renderExperienceChips = () => {
    if (!venue.experiences || !venue.experiences.length) return null;

    return (
      <div className="SearchResult__experience-chips">
        {venue.experiences.filter((e) => !e.hide_experience_chip).map((experience) => (
          <ExperienceChip
            key={experience.id}
            name={experience.name}
            onClick={() => handleExperienceChipClick(experience)}
          />
        ))}
      </div>
    );
  };

  const renderAvailability = () => {
    if (!showSlots) return null;

    const userIsNotGDAEligible = venue.gdaConciergeBooking && !userIsGda;
    const gdaVenueHasNoShiftsOrEvents = venue.gdaConciergeBooking && venue.availableServiceTypes.length === 0 && venueEvents.length === 0;
    if (userIsNotGDAEligible || gdaVenueHasNoShiftsOrEvents) {
      return (
        <Button onClick={toggleGdaConciergeModal} variant="gda-concierge">
          <GdaCircleLogo className="Button--gda-concierge__logo" />
          <TranslationProvider translationKey="contact_amex_to_book">
            <span>Contact Amex to Book</span>
          </TranslationProvider>
        </Button>
      );
    }

    if (venue.slots.length === 0 && venue.availableServiceTypes.length === 0 && !(isTockInventory && venue.tockNotifyUrl)) {
      return (
        <TranslationProvider translationKey="no_availability_for_party_message" translationValues={{ partySize: selectedSeats }}>
          <div className="SearchResult__no-availability body--sm color--text-secondary">{`Sorry, we don't currently have any tables available for ${selectedSeats}.`}</div>
        </TranslationProvider>
      );
    }

    return (
      <ReservationButtonList
        activeNotifications={activeNotifications}
        Analytics={{ event }}
        availableServiceTypes={venue.availableServiceTypes}
        context={context}
        experiences={venue.experiences}
        featureRecaptcha={venue.featureRecaptcha}
        hasTockExperiences={hasTockExperiences}
        isMobile={isMobile}
        isTockInventory={isTockInventory}
        limit={getButtonLimit()}
        onReservationSelect={onReservationSelect}
        partySize={selectedSeats}
        reservations={venue.slots}
        selectedDate={selectedDate}
        showNotify={showNotify}
        tockBusinessPageUrl={venue.tockBusinessPageUrl}
        tockBusinessSearchUrl={venue.tockBusinessSearchUrl}
        tockNotifyUrl={venue.tockNotifyUrl}
        venueCity={venue.location.name}
        venueId={venue.id}
        venueName={venue.name}
      />
    );
  };

  return (
    <div
      onMouseEnter={() => onVenueCardHover(venue.id)}
      className={cx('SearchResult', {
        'SearchResult--compact': compact,
        'SearchResult--mobile': isMobile,
        'SearchResult--short': isSuggestion,
        'SearchResult--bordered': showBorder,
        'SearchResult--row': layout === 'row',
      })}
    >
      {showPhoto && (
        <div
          className={cx('SearchResult__image', {
            'SearchResult__image-small': smallImage,
          })}
          style={venue.image ? { backgroundImage: `url(${venue.image})` } : null}
        />
      )}

      <div className={cx('SearchResult__content', {
        'SearchResult__content--photo-hidden': !showPhoto,
      })}
      >
        <div className="SearchResult__primary--container">
          <div className="SearchResult__primary">
            <div className="SearchResult__title--container">
              <div className="SearchResult__title-text">
                {eyebrow && <Eyebrow content={eyebrow} />}
                <Link
                  href={getVenueHref()}
                  {...((baseUrl !== undefined || isTockInventory) && { target: '_blank', rel: 'noopener' })}
                  className="SearchResult__container-link"
                  data-test-id={`${testId}-link-details`}
                  onClick={(e) => {
                    if (isTockInventory) {
                      e.preventDefault();
                      e.stopPropagation();
                      onVenueClick(e);
                      openTockUrl(getVenueHref(), dispatch);
                      return;
                    }
                    onVenueClick(e);
                  }}
                >
                  <h3 className="SearchResult__venue-name">
                    {venue.name}
                  </h3>
                </Link>
              </div>
              {renderFavoriteButton()}
            </div>
            <div className={cx('SearchResult__metadata', {
              'SearchResult__metadata-column': columnMetadata,
            })}
            >
              {showRating && !isTockInventory && (
                <div className="SearchResult__metadata--rating">
                  <Rating scale={5} score={venue.rating} totalRatings={venue.totalRatings} />
                  <span className="separator">&middot;</span>
                </div>
              )}

              <div className="SearchResult__metadata-cuisine-row">
                <div className="cuisine">{venue.cuisine}</div>
                {venue.cuisine && hasPriceRange && (
                  <span className="separator">&middot;</span>
                )}
                <div className="price">{priceRange}</div>
              </div>
              {renderCollections()}

              <div className="neighborhood">
                <Icon
                  component={pinSVG}
                  iconClass="pin"
                  ariaLabel={getTranslation({ translationKey: 'location', fallback: 'Location' })}
                  role="img"
                />
                {venue.location.neighborhood || venue.location.name}
                {venue.location.locality && (
                  `, ${venue.location.locality}`
                )}
              </div>
            </div>
            {isTockInventory && <TockBadge />}
          </div>
        </div>
        {renderWhyWeLikeIt(venueEvents)}
        {renderEvents(venueEvents)}
        {renderExperienceChips()}
        {renderAvailability()}
      </div>
    </div>
  );
};

SearchResult.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
  }).isRequired,
  activeNotifications: PropTypes.shape({
    [PropTypes.number]: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  baseUrl: PropTypes.string,
  canFavorite: PropTypes.bool,
  columnMetadata: PropTypes.bool,
  compact: PropTypes.bool,
  context: PropTypes.string,
  currency: PropTypes.string,
  event: PropTypes.func,
  eyebrow: PropTypes.string,
  isFavorite: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool,
  isSuggestion: PropTypes.bool,
  layout: PropTypes.oneOf(['card', 'row']),
  onFavoriteClick: PropTypes.func,
  onReservationButtonClick: PropTypes.func,
  onVenueCardHover: PropTypes.func,
  onVenueClick: PropTypes.func,
  reservationLimit: PropTypes.number,
  selectedDate: PropTypes.string,
  selectedSeats: PropTypes.number,
  showBadges: PropTypes.bool,
  showBorder: PropTypes.bool,
  showNotify: PropTypes.bool,
  showPhoto: PropTypes.bool,
  showRating: PropTypes.bool,
  showSlots: PropTypes.bool,
  smallImage: PropTypes.bool,
  userIsGda: PropTypes.bool,
  venue: PropTypes.shape({
    availableServiceTypes: PropTypes.arrayOf(PropTypes.string),
    experiences: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string.isRequired,
      hide_experience_chip: PropTypes.bool,
    })),
    collections: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      file_name: PropTypes.string,
      image: PropTypes.string,
      short_name: PropTypes.string,
      type_id: PropTypes.number,
    })),
    cuisine: PropTypes.string.isRequired,
    currency: PropTypes.string,
    favorite: PropTypes.bool,
    featureRecaptcha: PropTypes.bool,
    gdaConciergeBooking: PropTypes.bool,
    id: PropTypes.number.isRequired,
    image: PropTypes.string,
    isTockInventory: PropTypes.bool,
    tockBusinessPageUrl: PropTypes.string,
    tockBusinessSearchUrl: PropTypes.string,
    tockNotifyUrl: PropTypes.string,
    location: PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string,
      neighborhood: PropTypes.string.isRequired,
      locality: PropTypes.string,
      url_slug: PropTypes.string.isRequired,
      urlSlug: PropTypes.string.isRequired,
    }),
    name: PropTypes.string.isRequired,
    priceRange: PropTypes.string.isRequired,
    rating: PropTypes.number,
    slots: PropTypes.arrayOf(slotProps).isRequired,
    totalRatings: PropTypes.number,
    urlSlug: PropTypes.string.isRequired,
    whyWeLikeIt: PropTypes.shape({
      body: PropTypes.string,
      attribution: PropTypes.string,
    }),
  }).isRequired,
  venueEvents: PropTypes.arrayOf(PropTypes.shape({
    image_preview: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    url_slug: PropTypes.string.isRequired,
  })),
  urlSlug: PropTypes.string.isRequired,
  isAuthenticated: PropTypes.string.isRequired,
  showAuthModal: PropTypes.func.isRequired,
  setGatingError: PropTypes.func.isRequired,
};

SearchResult.defaultProps = {
  activeNotifications: {},
  baseUrl: undefined,
  canFavorite: false,
  columnMetadata: false,
  compact: false,
  context: undefined,
  currency: undefined,
  event: () => { },
  eyebrow: undefined,
  isMobile: false,
  isSuggestion: false,
  layout: 'card',
  onFavoriteClick: () => { },
  onReservationButtonClick: undefined,
  onVenueCardHover: () => { },
  onVenueClick: () => { },
  reservationLimit: 4,
  selectedDate: moment().format('YYYY-MM-DD'),
  selectedSeats: 2,
  showBadges: true,
  showBorder: true,
  showNotify: true,
  showPhoto: true,
  showRating: true,
  showSlots: true,
  smallImage: false,
  userIsGda: false,
  venueEvents: [],

};

SearchResult.displayName = 'SearchResult';
const mapStateToProps = (state, ownProps) => {
  const context = ownProps.context || getPageContext(window.location.pathname);
  return ({
    isFavorite: (context === LIST_DETAIL)
      ? !!(state.favoriteVenues.venuesByList[ownProps.venue.id])
      : !!(state.favoriteVenues.venuesByUser[ownProps.venue.id]),
  });
};

const mapDispatchToProps = (dispatch) => ({
  setGatingError: (data) => dispatch(setReservation(data)),
});

export { SearchResult as ComponentUnderTest };
export default connect(mapStateToProps, mapDispatchToProps)(SearchResult);
